// by freakms - ich schwöre feierlich ich bin ein tunichtgut
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Package, Plus, Pencil, Trash2, Search, Calculator,
  Euro, Percent, Box, Building2, Tag, ScanBarcode,
  LayoutGrid, List, ChevronDown, ChevronUp, X,
  TrendingUp, Loader2, ExternalLink, Store
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import BarcodeScanner from "../components/BarcodeScanner";

// ── Kategorien ────────────────────────────────────────────────
const CATEGORIES = ["Riegel", "Snack", "Getränk", "Keks", "Nuss", "Gummi", "Chips", "Sonstiges"];

// ── Lieferantenpreise Dialog ──────────────────────────────────
const SupplierPricesDialog = ({ product, suppliers, onClose }) => {
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPrice, setNewPrice] = useState({
    supplier_id: "", article_number: "",
    package_size: product?.package_size || 1,
    package_price_net: 0, package_price_gross: 0
  });

  useEffect(() => { if (product) fetchPrices(); }, [product]);

  const fetchPrices = async () => {
    try {
      const res = await axios.get(`${API}/product-supplier-prices/product/${product.id}`);
      setPrices(res.data);
    } catch { toast.error("Fehler beim Laden"); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!newPrice.supplier_id) { toast.error("Bitte Lieferant wählen"); return; }
    try {
      await axios.post(`${API}/product-supplier-prices`, { product_id: product.id, ...newPrice });
      toast.success("Gespeichert");
      fetchPrices();
      setNewPrice({ supplier_id: "", article_number: "", package_size: product?.package_size || 1, package_price_net: 0, package_price_gross: 0 });
    } catch { toast.error("Fehler"); }
  };

  const handleDelete = async (id) => {
    try { await axios.delete(`${API}/product-supplier-prices/${id}`); fetchPrices(); }
    catch { toast.error("Fehler"); }
  };

  return (
    <Dialog open={!!product} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Lieferantenpreise: {product?.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {prices.length > 0 && (
            <div className="space-y-2">
              {prices.map((p, idx) => (
                <div key={p.id} className={`flex items-center gap-3 p-3 rounded-lg border ${idx === 0 ? "border-emerald-300 bg-emerald-50" : "border-slate-200"}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{p.supplier_name}</span>
                      {idx === 0 && <Badge className="bg-emerald-500 text-xs">Günstigster</Badge>}
                    </div>
                    <div className="text-sm text-slate-500 flex flex-wrap gap-x-4">
                      <span>Art.-Nr.: {p.article_number || "–"}</span>
                      <span>VPE: {p.package_size} Stk</span>
                      <span className="font-mono">{p.package_price_net?.toFixed(2)}€ netto</span>
                    </div>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => handleDelete(p.id)}>
                    <Trash2 className="w-4 h-4 text-rose-500" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          <div className="border-t pt-4">
            <Label className="mb-3 block text-sm font-medium">Lieferant hinzufügen</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Lieferant *</Label>
                <Select value={newPrice.supplier_id} onValueChange={(v) => setNewPrice({ ...newPrice, supplier_id: v })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Wählen..." /></SelectTrigger>
                  <SelectContent>
                    {suppliers.filter(s => !prices.find(p => p.supplier_id === s.id)).map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Artikelnummer</Label>
                <Input className="mt-1" value={newPrice.article_number} onChange={(e) => setNewPrice({ ...newPrice, article_number: e.target.value })} placeholder="z.B. 123456" />
              </div>
              <div>
                <Label className="text-xs">VPE-Größe</Label>
                <Input className="mt-1" type="number" value={newPrice.package_size} onChange={(e) => setNewPrice({ ...newPrice, package_size: parseInt(e.target.value) || 1 })} />
              </div>
              <div>
                <Label className="text-xs">VPE-Preis Netto (€)</Label>
                <Input className="mt-1" type="number" step="0.01" value={newPrice.package_price_net} onChange={(e) => setNewPrice({ ...newPrice, package_price_net: parseFloat(e.target.value) || 0 })} />
              </div>
              <div>
                <Label className="text-xs">VPE-Preis Brutto (€)</Label>
                <Input className="mt-1" type="number" step="0.01" value={newPrice.package_price_gross} onChange={(e) => setNewPrice({ ...newPrice, package_price_gross: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>
            <Button onClick={handleSave} className="mt-3 bg-rose-600 hover:bg-rose-700">
              <Plus className="w-4 h-4 mr-2" />Hinzufügen
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};


// ── Preisvergleich Dialog (Claude AI) ────────────────────────
const PriceCompareDialog = ({ product, open, onClose }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && product) fetchPrices();
  }, [open, product]);

  const fetchPrices = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    const systemPrompt = [
      'Du bist ein Preisvergleichs-Assistent für einen Snackautomaten-Betreiber in Lüneburg.',
      'Recherchiere aktuelle Verkaufspreise bei deutschen Supermärkten (Aldi, Lidl, Rewe, Edeka, Penny, Kaufland, Netto).',
      'Antworte NUR mit einem gültigen JSON-Objekt ohne Markdown-Backticks:',
      '{"product":"Name","our_price":0.00,"stores":[{"name":"Aldi","price":0.00,"unit":"Stueck","note":"optional"}],"avg_market_price":0.00,"price_position":"guenstig oder marktueblich oder teuer","recommendation":"kurzer Satz"}.',
      'Wenn kein Preis gefunden: null. Nur JSON, kein Text davor oder danach.'
    ].join(' ');
    try {
      const response = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          tools: [{ type: "web_search_20250305", name: "web_search" }],
          system: systemPrompt,
          messages: [{
            role: "user",
            content: `Suche den aktuellen Preis für "${product.name}" ${product.ean ? `(EAN: ${product.ean})` : ""} ${product.category ? `(Kategorie: ${product.category})` : ""} in deutschen Supermärkten. Unser Verkaufspreis im Automaten ist ${product.price?.toFixed(2)}€.`
          }]
        })
      });
      const data = await response.json();
      const textBlocks = (data.content || []).filter(b => b.type === "text").map(b => b.text).join("");
      const cleaned = textBlocks.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(cleaned);
      setResult(parsed);
    } catch (err) {
      setError("Preisvergleich konnte nicht geladen werden. Bitte später erneut versuchen.");
    } finally {
      setLoading(false);
    }
  };

  const positionColor = {
    "günstig": "text-emerald-600 bg-emerald-50 border-emerald-200",
    "marktüblich": "text-blue-600 bg-blue-50 border-blue-200",
    "teuer": "text-amber-600 bg-amber-50 border-amber-200"
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            Preisvergleich
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Produkt-Header */}
          <div className="bg-slate-50 rounded-md p-3 flex items-center justify-between">
            <div>
              <div className="font-medium text-sm">{product?.name}</div>
              {product?.category && <div className="text-xs text-slate-400">{product.category}</div>}
            </div>
            <div className="text-right">
              <div className="text-xs text-slate-400">Unser Preis</div>
              <div className="text-lg font-heading font-bold text-[#DC2626]">{product?.price?.toFixed(2)}€</div>
            </div>
          </div>

          {/* Ladestate */}
          {loading && (
            <div className="flex flex-col items-center gap-3 py-8">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              <div className="text-sm text-slate-500">Aktuelle Marktpreise werden abgerufen...</div>
            </div>
          )}

          {/* Fehler */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Ergebnis */}
          {result && !loading && (
            <div className="space-y-3">
              {/* Marktposition */}
              {result.price_position && (
                <div className={`flex items-center justify-between px-3 py-2 rounded border text-sm font-medium ${positionColor[result.price_position] || "text-slate-600 bg-slate-50 border-slate-200"}`}>
                  <span>Preisposition</span>
                  <span className="capitalize">{result.price_position}</span>
                </div>
              )}

              {/* Store-Preise */}
              {result.stores?.filter(s => s.price != null).length > 0 && (
                <div>
                  <div className="text-xs font-medium text-slate-500 mb-1.5">Aktuelle Marktpreise</div>
                  <div className="space-y-1.5">
                    {result.stores.filter(s => s.price != null).sort((a, b) => a.price - b.price).map((store, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded bg-slate-50 border border-slate-100 text-sm">
                        <div className="flex items-center gap-2">
                          <Store className="w-3.5 h-3.5 text-slate-400" />
                          <span className="font-medium">{store.name}</span>
                          {store.note && <span className="text-xs text-slate-400">{store.note}</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold">{store.price.toFixed(2)}€</span>
                          {product?.price && (
                            <span className={`text-xs px-1.5 py-0.5 rounded ${store.price < product.price ? "bg-emerald-100 text-emerald-700" : store.price > product.price ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                              {store.price < product.price ? `${((product.price - store.price) / product.price * 100).toFixed(0)}% günstiger` : store.price > product.price ? `${((store.price - product.price) / store.price * 100).toFixed(0)}% teurer` : "gleich"}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Durchschnitt */}
              {result.avg_market_price && (
                <div className="flex items-center justify-between text-sm border-t pt-2">
                  <span className="text-slate-500">Ø Marktpreis</span>
                  <span className="font-mono font-bold">{result.avg_market_price.toFixed(2)}€</span>
                </div>
              )}

              {/* Empfehlung */}
              {result.recommendation && (
                <div className="bg-blue-50 border border-blue-200 rounded p-2.5 text-sm text-blue-700">
                  {result.recommendation}
                </div>
              )}

              {/* Neu laden */}
              <Button variant="outline" size="sm" className="w-full text-xs" onClick={fetchPrices}>
                <TrendingUp className="w-3 h-3 mr-1" /> Neu abfragen
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ── Produktkarte (Grid-Ansicht) ───────────────────────────────
const ProductCard = ({ product, onEdit, onDelete, onSupplierPrices, onPriceCompare }) => {
  const [imgError, setImgError] = useState(false);

  return (
    <motion.div layout initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }} transition={{ duration: 0.15 }}>
      <Card className="border-slate-200 hover:shadow-md transition-all overflow-hidden group cursor-pointer" onClick={() => onEdit(product)}>
        {/* Produktbild – quadratisch, kein aspect-video */}
        <div className="relative bg-slate-50" style={{ paddingBottom: "75%" }}>
          <div className="absolute inset-0">
            {product.image_url && !imgError ? (
              <img
                src={product.image_url}
                alt={product.name}
                onError={() => setImgError(true)}
                className="w-full h-full object-contain p-2"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                <Package className="w-10 h-10 text-slate-200" />
                <span className="text-xs text-slate-300">{product.category || ""}</span>
              </div>
            )}
          </div>
          {/* Hover-Overlay nur mit Löschen */}
          <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              size="icon"
              variant="destructive"
              className="h-7 w-7"
              onClick={(e) => { e.stopPropagation(); onDelete(product); }}
              data-testid={`delete-product-${product.id}`}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
          {/* Kategorie-Badge */}
          {product.category && (
            <div className="absolute bottom-2 left-2">
              <span className="text-[10px] bg-white/90 text-slate-600 px-1.5 py-0.5 rounded border border-slate-200">
                {product.category}
              </span>
            </div>
          )}
        </div>
        {/* Karten-Body – minimal */}
        <CardContent className="p-3">
          <h3 className="font-medium text-slate-900 text-sm leading-tight truncate">{product.name}</h3>
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-base font-heading font-bold text-[#DC2626]">
              {product.price?.toFixed(2) || "0.00"}€
            </span>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-slate-400 hover:text-slate-600"
                onClick={(e) => { e.stopPropagation(); onSupplierPrices(product); }}
              >
                <Building2 className="w-3 h-3 mr-1" />
                Lieferant
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-blue-400 hover:text-blue-600"
                onClick={(e) => { e.stopPropagation(); onPriceCompare(product); }}
                title="Marktpreise vergleichen"
              >
                <TrendingUp className="w-3 h-3" />
              </Button>
            </div>
          </div>
          {/* Nur Pfand wenn relevant */}
          {product.deposit > 0 && (
            <div className="text-xs text-amber-600 mt-1">+ {product.deposit.toFixed(2)}€ Pfand</div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

// ── Produkt-Zeile (Listen-Ansicht) ────────────────────────────
const ProductRow = ({ product, onEdit, onDelete, onSupplierPrices, onPriceCompare }) => {
  const [imgError, setImgError] = useState(false);
  return (
    <motion.div layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.1 }}>
      <div className="flex items-center gap-3 py-2.5 px-3 border-b border-slate-100 hover:bg-slate-50 cursor-pointer group" onClick={() => onEdit(product)}>
        {/* Thumbnail */}
        <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center shrink-0 overflow-hidden">
          {product.image_url && !imgError ? (
            <img src={product.image_url} alt={product.name} onError={() => setImgError(true)} className="w-full h-full object-contain" loading="lazy" />
          ) : (
            <Package className="w-5 h-5 text-slate-300" />
          )}
        </div>
        {/* Name + Kategorie */}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm text-slate-900 truncate">{product.name}</div>
          <div className="text-xs text-slate-400 flex items-center gap-2">
            {product.category && <span>{product.category}</span>}
            {product.ean && <span className="font-mono">{product.ean}</span>}
          </div>
        </div>
        {/* Preis */}
        <div className="text-right shrink-0">
          <div className="font-heading font-bold text-[#DC2626] text-sm">{product.price?.toFixed(2) || "0.00"}€</div>
          {product.deposit > 0 && <div className="text-xs text-amber-500">+{product.deposit.toFixed(2)}€ Pfand</div>}
        </div>
        {/* Aktionen */}
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); onSupplierPrices(product); }}>
            <Building2 className="w-3.5 h-3.5 text-slate-400" />
          </Button>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); onPriceCompare(product); }}>
            <TrendingUp className="w-3.5 h-3.5 text-blue-400" />
          </Button>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); onDelete(product); }}>
            <Trash2 className="w-3.5 h-3.5 text-rose-500" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

// ── Produkt-Formular ──────────────────────────────────────────
const ProductForm = ({ product, onSave, onClose, catalogItems = [] }) => {
  const [name, setName] = useState(product?.name || "");
  const [category, setCategory] = useState(product?.category || "");
  const [imageUrl, setImageUrl] = useState(product?.image_url || "");
  const [ean, setEan] = useState(product?.ean || "");
  const [packagePriceNet, setPackagePriceNet] = useState(product?.package_price_net?.toString() || "");
  const [packageSize, setPackageSize] = useState(product?.package_size?.toString() || "1");
  const [vatRate, setVatRate] = useState(product?.vat_rate?.toString() || "19");
  const [markupPercent, setMarkupPercent] = useState(product?.markup_percent?.toString() || "30");
  const [deposit, setDeposit] = useState(product?.deposit?.toString() || "0");
  const [minStock, setMinStock] = useState(product?.min_stock?.toString() || "10");
  const [targetStock, setTargetStock] = useState(product?.target_stock?.toString() || "50");
  const [saving, setSaving] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [priceCompareProduct, setPriceCompareProduct] = useState(null);
  const [priceCompareOpen, setPriceCompareOpen] = useState(false);
  const [catalogPickerOpen, setCatalogPickerOpen] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [linkedCatalogItemId, setLinkedCatalogItemId] = useState(product?.catalog_item_id || null);

  const linkedCatalogItem = catalogItems.find(c => c.id === linkedCatalogItemId) || null;

  const handleCatalogLink = (item) => {
    setLinkedCatalogItemId(item.id);
    // package_price_net aus Katalog bevorzugen, sonst price_net (VPE-Preis)
    const priceNet = item.package_price_net || item.price_net || 0;
    setPackagePriceNet(priceNet.toString());
    setPackageSize(item.package_size?.toString() || "1");
    setVatRate(item.vat_rate?.toString() || "19");
    setCatalogPickerOpen(false);
    setCatalogSearch("");
    toast.success(`Preise aus Katalog übernommen: ${item.name}`);
  };

  const handleCatalogUnlink = () => {
    setLinkedCatalogItemId(null);
  };

  const filteredCatalogItems = catalogItems.filter(c =>
    !catalogSearch ||
    c.name?.toLowerCase().includes(catalogSearch.toLowerCase()) ||
    c.article_number?.toLowerCase().includes(catalogSearch.toLowerCase())
  );

  const calcPackagePriceGross = () => (parseFloat(packagePriceNet) || 0) * (1 + (parseFloat(vatRate) || 19) / 100);
  const calcPurchasePriceNet  = () => (parseFloat(packagePriceNet) || 0) / (parseInt(packageSize) || 1);
  const calcPurchasePriceGross= () => calcPackagePriceGross() / (parseInt(packageSize) || 1);
  const calcSalePrice         = () => calcPurchasePriceGross() * (1 + (parseFloat(markupPercent) || 0) / 100) + (parseFloat(deposit) || 0);

  const handleBarcodeScan = (code) => { setEan(code); toast.success(`Barcode: ${code}`); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name ist erforderlich"); return; }
    setSaving(true);
    try {
      const data = {
        name: name.trim(), category: category.trim(), image_url: imageUrl.trim(), ean: ean.trim(),
        package_price_net: parseFloat(packagePriceNet) || 0,
        package_size: parseInt(packageSize) || 1,
        vat_rate: parseFloat(vatRate) || 19,
        markup_percent: parseFloat(markupPercent) || 30,
        deposit: parseFloat(deposit) || 0,
        min_stock: parseInt(minStock) || 10,
        target_stock: parseInt(targetStock) || 50,
        catalog_item_id: linkedCatalogItemId || null,
      };
      if (product) { await axios.put(`${API}/products/${product.id}`, data); toast.success("Aktualisiert"); }
      else          { await axios.post(`${API}/products`, data);             toast.success("Erstellt"); }
      onClose();
      setTimeout(() => onSave(), 100);
    } catch { toast.error("Fehler beim Speichern"); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Tabs defaultValue="basic" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="basic">Basis</TabsTrigger>
          <TabsTrigger value="purchase">Einkauf</TabsTrigger>
          <TabsTrigger value="sale">Verkauf</TabsTrigger>
        </TabsList>

        {/* ── Basis ── */}
        <TabsContent value="basic" className="space-y-3 pt-4">
          <div>
            <Label className="text-xs">Produktname *</Label>
            <Input className="mt-1" value={name} onChange={(e) => setName(e.target.value)} placeholder="z.B. Mars Riegel" autoFocus data-testid="product-name-input" />
          </div>
          <div>
            <Label className="text-xs">Kategorie</Label>
            <div className="flex gap-2 mt-1">
              <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="z.B. Süßigkeiten" data-testid="product-category-input" className="flex-1" />
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="w-32"><SelectValue placeholder="Vorlage" /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Bild-URL</Label>
            <Input className="mt-1" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://..." data-testid="product-image-input" />
            {imageUrl && (
              <div className="mt-2 w-16 h-16 rounded border border-slate-200 overflow-hidden bg-slate-50">
                <img src={imageUrl} alt="" className="w-full h-full object-contain" onError={(e) => e.target.style.display="none"} />
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1"><ScanBarcode className="w-3 h-3" /> EAN / Barcode</Label>
            <div className="flex gap-2 mt-1">
              <Input value={ean} onChange={(e) => setEan(e.target.value)} placeholder="4001234567890" data-testid="product-ean-input" className="flex-1 font-mono" />
              <Button type="button" variant="outline" onClick={() => setScannerOpen(true)} data-testid="product-scan-btn">
                <ScanBarcode className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* ── Einkauf ── */}
        <TabsContent value="purchase" className="space-y-3 pt-4">
          {/* Katalog-Verknüpfung */}
          {catalogItems.length > 0 && (
            <div>
              <Label className="text-xs flex items-center gap-1">
                <Box className="w-3 h-3" /> Aus Katalog
              </Label>
              {linkedCatalogItem ? (
                <div className="mt-1 flex items-center justify-between p-2 bg-emerald-50 border border-emerald-200 rounded-md">
                  <div className="text-xs">
                    <span className="font-medium text-emerald-800">{linkedCatalogItem.name}</span>
                    <span className="text-emerald-600 ml-2">
                      {(linkedCatalogItem.package_price_net || linkedCatalogItem.price_net || 0).toFixed(2)}€ netto / {linkedCatalogItem.package_size} Stück
                    </span>
                  </div>
                  <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-xs text-slate-400 hover:text-red-500" onClick={handleCatalogUnlink}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <Button type="button" variant="outline" size="sm" className="mt-1 h-8 text-xs" onClick={() => setCatalogPickerOpen(true)}>
                  <Plus className="w-3 h-3 mr-1" /> Aus Katalog wählen
                </Button>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Stück pro VPE</Label>
              <Input className="mt-1" type="number" min="1" value={packageSize} onChange={(e) => setPackageSize(e.target.value)} placeholder="24" data-testid="product-package-size-input" />
            </div>
            <div>
              <Label className="text-xs">VPE-Preis Netto (€)</Label>
              <Input className="mt-1" type="number" step="0.01" min="0" value={packagePriceNet} onChange={(e) => setPackagePriceNet(e.target.value)} placeholder="12.50" data-testid="product-package-price-input" />
            </div>
            <div>
              <Label className="text-xs">MwSt (%)</Label>
              <Input className="mt-1" type="number" step="0.1" min="0" value={vatRate} onChange={(e) => setVatRate(e.target.value)} placeholder="19" data-testid="product-vat-input" />
            </div>
          </div>
          <div className="bg-blue-50 rounded-md p-3 grid grid-cols-3 gap-2 text-xs">
            <div><span className="text-blue-500">VPE Brutto</span><div className="font-mono font-bold">{calcPackagePriceGross().toFixed(2)}€</div></div>
            <div><span className="text-blue-500">Stück Netto</span><div className="font-mono font-bold">{calcPurchasePriceNet().toFixed(4)}€</div></div>
            <div><span className="text-blue-500">Stück Brutto</span><div className="font-mono font-bold">{calcPurchasePriceGross().toFixed(4)}€</div></div>
          </div>
        </TabsContent>

        {/* ── Verkauf ── */}
        <TabsContent value="sale" className="space-y-3 pt-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs flex items-center gap-1"><Percent className="w-3 h-3" />Aufschlag (%)</Label>
              <Input className="mt-1" type="number" step="1" min="0" value={markupPercent} onChange={(e) => setMarkupPercent(e.target.value)} placeholder="30" data-testid="product-markup-input" />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><Euro className="w-3 h-3" />Pfand (€)</Label>
              <Input className="mt-1" type="number" step="0.01" min="0" value={deposit} onChange={(e) => setDeposit(e.target.value)} placeholder="0.25" data-testid="product-deposit-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Mindestbestand</Label>
              <Input className="mt-1" type="number" min="0" value={minStock} onChange={(e) => setMinStock(e.target.value)} placeholder="10" data-testid="product-min-stock-input" />
              <p className="text-xs text-slate-400 mt-1">Bestellvorschlag ab hier</p>
            </div>
            <div>
              <Label className="text-xs">Zielbestand</Label>
              <Input className="mt-1" type="number" min="0" value={targetStock} onChange={(e) => setTargetStock(e.target.value)} placeholder="50" data-testid="product-target-stock-input" />
              <p className="text-xs text-slate-400 mt-1">Menge bei Nachbestellung</p>
            </div>
          </div>
          <div className="bg-emerald-50 rounded-md p-3 flex items-center justify-between">
            <span className="text-sm text-emerald-700">Berechneter Verkaufspreis</span>
            <span className="text-2xl font-heading font-bold text-emerald-600">{calcSalePrice().toFixed(2)}€</span>
          </div>
        </TabsContent>
      </Tabs>

      <div className="flex gap-3 pt-2 border-t">
        <Button type="button" variant="outline" onClick={onClose} className="flex-1">Abbrechen</Button>
        <Button type="submit" disabled={saving} className="flex-1 bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="product-save-btn">
          {saving ? "Speichern..." : product ? "Aktualisieren" : "Erstellen"}
        </Button>
      </div>

      <BarcodeScanner isOpen={scannerOpen} onClose={() => setScannerOpen(false)} onScan={handleBarcodeScan} title="Produkt-Barcode scannen" />

      {/* Katalog-Picker Dialog */}
      <Dialog open={catalogPickerOpen} onOpenChange={setCatalogPickerOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Aus Katalog wählen</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Suchen nach Name oder Artikelnummer..."
              value={catalogSearch}
              onChange={(e) => setCatalogSearch(e.target.value)}
              autoFocus
            />
            <div className="max-h-72 overflow-y-auto space-y-1">
              {filteredCatalogItems.length === 0 ? (
                <div className="text-sm text-slate-400 text-center py-4">Keine Treffer</div>
              ) : filteredCatalogItems.map(item => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleCatalogLink(item)}
                  className="w-full text-left p-2 rounded hover:bg-slate-100 transition-colors"
                >
                  <div className="text-sm font-medium">{item.name}</div>
                  <div className="text-xs text-slate-500 flex gap-3 mt-0.5">
                    <span>Art. {item.article_number}</span>
                    <span>{(item.package_price_net || item.price_net || 0).toFixed(2)}€ netto / {item.package_size} Stk.</span>
                    <span>{item.vat_rate}% MwSt</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </form>
  );
};

// ── Haupt-Seite ───────────────────────────────────────────────
const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [viewMode, setViewMode] = useState("grid"); // grid | list
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [deleteProduct, setDeleteProduct] = useState(null);
  const [supplierPricesProduct, setSupplierPricesProduct] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [priceCompareProduct, setPriceCompareProduct] = useState(null);
  const [priceCompareOpen, setPriceCompareOpen] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [productsRes, suppliersRes, catalogRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/suppliers`),
        axios.get(`${API}/supplier-catalog`)
      ]);
      setProducts(productsRes.data);
      setSuppliers(suppliersRes.data);
      setCatalogItems(catalogRes.data);
    } catch { toast.error("Fehler beim Laden"); }
    finally { setLoading(false); }
  };

  const fetchProducts = async () => {
    try { const res = await axios.get(`${API}/products`); setProducts(res.data); }
    catch { toast.error("Fehler"); }
  };

  const handleDelete = async () => {
    if (!deleteProduct) return;
    try { await axios.delete(`${API}/products/${deleteProduct.id}`); toast.success("Gelöscht"); fetchProducts(); }
    catch { toast.error("Fehler beim Löschen"); }
    finally { setDeleteProduct(null); }
  };

  const handleBarcodeScan = async (code) => {
    try {
      const res = await axios.get(`${API}/products/by-ean/${code}`);
      if (res.data) { setSearch(code); toast.success(`Gefunden: ${res.data.name}`); }
    } catch { toast.error(`Kein Produkt mit EAN ${code}`); setSearch(code); }
  };

  // Alle verfügbaren Kategorien
  const allCategories = [...new Set(products.map(p => p.category).filter(Boolean))].sort();

  const filtered = products.filter(p => {
    const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.category?.toLowerCase().includes(search.toLowerCase()) || p.ean?.toLowerCase().includes(search.toLowerCase());
    const matchCat = categoryFilter === "all" || p.category === categoryFilter;
    return matchSearch && matchCat;
  });

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#DC2626]" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="products-page">
      <SupplierPricesDialog product={supplierPricesProduct} suppliers={suppliers} onClose={() => setSupplierPricesProduct(null)} />
      <PriceCompareDialog product={priceCompareProduct} open={priceCompareOpen} onClose={() => { setPriceCompareOpen(false); setPriceCompareProduct(null); }} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">Produkte</h1>
          <p className="text-slate-500 mt-0.5">{products.length} im Sortiment · {filtered.length} angezeigt</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#DC2626] hover:bg-[#B91C1C]" onClick={() => setEditProduct(null)} data-testid="add-product-btn">
              <Plus className="w-4 h-4 mr-2" />Neues Produkt
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">{editProduct ? "Produkt bearbeiten" : "Neues Produkt"}</DialogTitle>
            </DialogHeader>
            <ProductForm product={editProduct} onSave={fetchProducts} onClose={() => setDialogOpen(false)} catalogItems={catalogItems} />
          </DialogContent>
        </Dialog>
      </div>

      {/* Suche + Filter + Ansicht */}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Name, Kategorie, EAN..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
            data-testid="product-search-input"
          />
          {search && (
            <button className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => setSearch("")}>
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-36 h-9">
            <Tag className="w-3 h-3 mr-1 text-slate-400" />
            <SelectValue placeholder="Kategorie" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Kategorien</SelectItem>
            {allCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setScannerOpen(true)} title="Barcode scannen" data-testid="product-scan-search-btn">
          <ScanBarcode className="w-4 h-4" />
        </Button>
        <div className="flex border rounded-md overflow-hidden h-9">
          <button className={`px-2.5 flex items-center ${viewMode === "grid" ? "bg-slate-100 text-slate-900" : "text-slate-400 hover:text-slate-600"}`} onClick={() => setViewMode("grid")} title="Kachelansicht">
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button className={`px-2.5 flex items-center border-l ${viewMode === "list" ? "bg-slate-100 text-slate-900" : "text-slate-400 hover:text-slate-600"}`} onClick={() => setViewMode("list")} title="Listenansicht">
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      <BarcodeScanner isOpen={scannerOpen} onClose={() => setScannerOpen(false)} onScan={handleBarcodeScan} title="Produkt per Barcode suchen" />

      {/* Produkte */}
      {filtered.length > 0 ? (
        viewMode === "grid" ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            <AnimatePresence mode="popLayout">
              {filtered.map(product => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onEdit={(p) => { setEditProduct(p); setDialogOpen(true); }}
                  onDelete={setDeleteProduct}
                  onSupplierPrices={setSupplierPricesProduct}
                  onPriceCompare={(p) => { setPriceCompareProduct(p); setPriceCompareOpen(true); }}
                />
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <Card className="border-slate-200 overflow-hidden">
            <AnimatePresence>
              {filtered.map(product => (
                <ProductRow
                  key={product.id}
                  product={product}
                  onEdit={(p) => { setEditProduct(p); setDialogOpen(true); }}
                  onDelete={setDeleteProduct}
                  onSupplierPrices={setSupplierPricesProduct}
                  onPriceCompare={(p) => { setPriceCompareProduct(p); setPriceCompareOpen(true); }}
                />
              ))}
            </AnimatePresence>
          </Card>
        )
      ) : (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Package className="w-12 h-12 mx-auto text-slate-200 mb-4" />
            <p className="text-slate-500">{search || categoryFilter !== "all" ? "Keine Produkte gefunden" : "Noch keine Produkte angelegt"}</p>
            {!search && categoryFilter === "all" && (
              <Button className="mt-4 bg-[#DC2626] hover:bg-[#B91C1C]" onClick={() => { setEditProduct(null); setDialogOpen(true); }}>
                <Plus className="w-4 h-4 mr-2" />Erstes Produkt anlegen
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteProduct} onOpenChange={() => setDeleteProduct(null)}>
        <AlertDialogContent className="max-w-[95vw] sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Produkt löschen?</AlertDialogTitle>
            <AlertDialogDescription>„{deleteProduct?.name}" wird dauerhaft gelöscht.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="confirm-delete-btn">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProductsPage;
